/**
 * Pure helpers for "Use DCA in Claude" (MCP connections) and the per-campaign
 * "Who writes the messages" switch. Kept free of React so they are unit tested.
 */

export type McpAccessLevel = 'read' | 'read_write'

export interface McpConnection {
  readonly id: string
  readonly prefix: string
  readonly label: string
  readonly created_at: string | null
  readonly expires_at: string | null
  readonly last_used_at: string | null
  readonly write: boolean
  /** "Claude checks its own messages": may skip DealCloser's quality gate. */
  readonly skip_qg?: boolean
  readonly expired: boolean
}

export interface McpSettingsResponse {
  readonly connections: readonly McpConnection[]
  readonly active_count: number
  readonly max_active: number
  readonly writes_enabled: boolean
  readonly plan_access: McpAccessLevel
  readonly eligible: boolean
  readonly mcp_url: string
}

export interface McpCreateResponse {
  readonly token: string
  readonly connector_url: string
  readonly mcp_url: string
  readonly connection: McpConnection
  readonly writes_enabled: boolean
  readonly note?: string
}

/** settings.message_writer: DealCloser AI, the user's Claude, or the user's own templates (card S1/S3). */
export type MessageWriter = 'dca' | 'mcp' | 'template'

/**
 * One wording for the three writers, shared by the create wizard's Method
 * step and the campaign page's "Who writes the messages" card.
 */
export const MESSAGE_WRITER_COPY: Readonly<Record<MessageWriter, { readonly label: string; readonly hint: string }>> = {
  dca: {
    label: 'AI writes each message',
    hint: 'DealCloser AI writes every message for each lead, in your writing style.',
  },
  template: {
    label: 'Use a template',
    hint: "Your own messages, sent exactly as written with each lead's details filled in. DealCloser AI still answers replies.",
  },
  mcp: {
    label: 'Your Claude',
    hint: 'Your connected Claude writes the DMs, follow-ups and replies. DealCloser still sends the connection requests.',
  },
}

/** Order the writers are offered in. */
export const MESSAGE_WRITER_ORDER: readonly MessageWriter[] = ['dca', 'template', 'mcp']

export const EXPIRY_OPTIONS = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
] as const

/** Fallback hours for "Your Claude writes" campaigns. 0 = never hand back. */
export const FALLBACK_OPTIONS = [
  { hours: 0, label: 'Off' },
  { hours: 12, label: 'After 12 hours' },
  { hours: 24, label: 'After 24 hours' },
  { hours: 48, label: 'After 48 hours' },
] as const

export const DEFAULT_FALLBACK_HOURS = 24

/** Where the Settings section lives, for links from campaign screens. */
export const CLAUDE_SETTINGS_PATH = '/settings?tab=claude'

/**
 * The ready-made routine prompt, word for word the one in docs/mcp/claude-routine.md
 * (keep the two in step). 'draft' is the review-first version: everything Claude
 * writes waits in Inbox > Review instead of going out.
 */
export function routinePrompt(mode: 'send' | 'draft'): string {
  return [
    'Do my DealCloser messaging using the DealCloser connector.',
    `Use the run_dca_messaging procedure (call get_routine_instructions if you cannot see MCP prompts) in ${mode === 'send' ? 'SEND' : 'DRAFT ONLY'} mode, at most 25 messages this run.`,
    "Work replies first, then check-ins, then outreach. Follow each lead's writing_rules exactly, check every message before submitting, and never work around a rejection.",
    'When a lead wants a call, use get_booking_options and book_meeting, then confirm. Record every clear no, opt-out, booking made elsewhere or "check back later" with update_lead, and hand anything sensitive or uncertain to me with update_lead hand_to_human.',
    'End with get_messaging_status and a short summary: sent, held for review, booked, outcomes, skipped (and why), and next_due_at.',
  ].join('\n')
}

/**
 * The campaigns API spreads the raw row, so `settings` arrives as the stored
 * JSON string (or, from some callers, an object). Parse it defensively.
 */
export function parseCampaignSettings(settings: unknown): Record<string, unknown> {
  if (settings && typeof settings === 'object') return settings as Record<string, unknown>
  if (typeof settings !== 'string' || settings.trim() === '') return {}
  try {
    const parsed = JSON.parse(settings) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

export function readMessageWriter(campaign: { readonly settings?: unknown }): MessageWriter {
  const w = parseCampaignSettings(campaign.settings)['message_writer']
  return w === 'mcp' || w === 'template' ? w : 'dca'
}

/**
 * True when any of the campaign's steps carries template text (card S3), so
 * "Your templates" can be offered on the campaign page. Reads the top-level
 * steps and, for rows saved before card S2, a nested settings.sequence copy.
 */
export function hasTemplateText(campaign: { readonly settings?: unknown }): boolean {
  const s = parseCampaignSettings(campaign.settings)
  const nested = s['sequence'] && typeof s['sequence'] === 'object' ? (s['sequence'] as Record<string, unknown>)['steps'] : undefined
  return [s['steps'], nested].some(
    (steps) =>
      Array.isArray(steps) &&
      steps.some((step) => {
        const t = step && typeof step === 'object' ? (step as Record<string, unknown>)['template'] : undefined
        return typeof t === 'string' && t.trim().length > 0
      }),
  )
}

export function readFallbackHours(campaign: { readonly settings?: unknown }): number {
  const raw = parseCampaignSettings(campaign.settings)['mcp_fallback_hours']
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN
  return Number.isFinite(n) && n >= 0 ? n : DEFAULT_FALLBACK_HOURS
}

/** The settings patch the campaign PUT route merges into the stored blob. */
export function messageWriterPatch(writer: MessageWriter, fallbackHours: number): Record<string, unknown> {
  return { message_writer: writer, mcp_fallback_hours: fallbackHours }
}

export function claudeCodeCommand(connectorUrl: string): string {
  return `claude mcp add --transport http dca ${connectorUrl}`
}

export function activeConnections(list: readonly McpConnection[]): McpConnection[] {
  return list.filter((c) => !c.expired)
}

/** True when the user has at least one live connection that is allowed to write. */
export function hasWriteConnection(list: readonly McpConnection[]): boolean {
  return list.some((c) => !c.expired && c.write)
}
